// src/services/sourceLogTemplates.js
//
// SourceX — the standing paper LOGS ("Add Log").
//
// A log is not a visit. A coordinator prints one at the start of the study,
// fills it in by hand as things happen over the following months, and
// photocopies another page when they run out of rows. It belongs to the
// patient for the whole study, not to any one visit — which is why Add Log
// puts each of these in its OWN section rather than inside a visit, and why
// their footers print a blank "Page __ of __" instead of a live page number.
//
// This file is DATA ONLY, exactly like sourceQuestionTemplates.js. Each entry
// is normalized through makeSection / makeEvent / makeQuestion at insert time
// (see SectionEditor.addLog), so every inserted field gets a fresh id and the
// canonical v2 shape. Nothing here is a live question and nothing here writes.
//
// The `unavailable` convention is the same one sourceQuestionTemplates.js
// uses: a log carrying it is listed, greyed out and un-pickable, with that
// string shown as the reason. Enabling one is deleting its `unavailable` line.
//
// MEASURED FROM THE SOURCE PDFs
//
// The Con Med and Medical/Surgical History logs are transcriptions of
// `ConMedLog.pdf` and `Medical-Surgical History Log.pdf` in the repo root. Both
// are 11 x 8.5in LANDSCAPE, which is why every log prints landscape here. Their
// column widths, row counts and row heights below were measured off those two
// pages (rasterised, then scanned for the grid rules) rather than estimated —
// so the printed RegX page is the same form, not a lookalike.
//
// LAYOUT NOTES — why these logs carry fields ordinary questions don't:
//   • `orientation: "landscape"` — these logs are 6-10 columns wide. On a
//     portrait page the exporters would split them into stacked sub-tables (see
//     splitThresholdFor); landscape keeps each log as the single grid the paper
//     form is.
//   • `rowHeightPt` — writing space per row, in points, from the PDF.
//   • `numbered` / `numberHeader` — the leading narrow "No." column. Its cells
//     print EMPTY, as the paper form does; the coordinator numbers entries.
//   • column `width` — percent shares of the table. All-or-nothing per table;
//     see columnWidthPercents.
//
// FITTING ONE PAGE. A coordinator prints a log once and photocopies it, so a
// log that spilled onto a second page would defeat the blank "Page __ of __".
// The landscape content area is 6.6in. Budget, in inches:
//
//   title 0.30 + [question 0.35] + header row 0.35-0.50
//     + rows x rowHeightPt + closing signature 0.37   <=  6.60
//
// Con Med:  0.30 + 0.35 + 15 x 25pt (0.347in) = 6.26in
// Med/Surg: 0.30 + 0.35 + 0.50 + 9 x 34pt (0.472in) = 5.40in
// Exacerb.: 0.30 + 0.80 (two header rows) + 12 x 26pt (0.361in) = 5.83in
//
// All three have slack, and `the printed log page` in the test file computes
// this same budget and fails if any log exceeds the page.
//
// The header row is the other half of fitting: the .docx exporter used to
// ROTATE column headers to read bottom-to-top whenever a column got narrow,
// which cost up to 2.2in of page and is what pushed the Con Med log onto a
// second page. A table with explicit widths never rotates now — its layout
// came off a real form whose headers are horizontal. See buildTable.

import { normFieldType } from "./templateSchema";

// Printed as a checkbox pair on the paper forms, same as in
// sourceQuestionTemplates.js.
const YES_NO = ["Yes", "No"];

/**
 * The one line that ends EVERY log page, printed by both exporters in place of
 * a visit's closing block. Lives here, with the logs, rather than in a
 * renderer, so the two exporters render the same sentence.
 */
export const LOG_CLOSING_LABEL =
  "Investigator/Sub-Investigator Signature at end of study:";

/**
 * @typedef {object} SourceLogTemplate
 * @property {string} id     stable key for the dropdown
 * @property {string} label  the log's official name. Used three ways: the
 *   dropdown entry, the section + event name, and — because the event name is
 *   what prints as the centred bold page title — the heading on the page.
 * @property {string} [orientation]  "landscape" for a wide log
 * @property {string} [unavailable]  why this log can't be used yet
 * @property {Array<object>} questions  makeQuestion-shaped seeds (no ids)
 */

/** @type {SourceLogTemplate[]} */
export const SOURCE_LOG_TEMPLATES = [
  {
    id: "con-med-log",
    label: "Concomitant Medication Log",
    orientation: "landscape",
    questions: [
      {
        type: "Table",
        // Identical to the log's name on purpose: validateTemplate requires
        // every question to have text, but the centred page title already says
        // this. Both exporters drop a log question's label when it matches the
        // event name, so it prints once — see questionHtml / questionElements.
        question: "Concomitant Medication Log",
        // 15 rows, as the PDF has. Its own rows are 26.5pt and it squeezes the
        // last three shorter to fit; these are a uniform 25pt, which is within
        // a pen-stroke of the same and leaves the budget above real slack
        // instead of landing on 6.62in against a 6.60in page.
        minRows: 15,
        rowHeightPt: 25,
        // The "No." column is the exporters' own narrow lead column. It is
        // deliberately NOT one of the nine data columns below, and the width
        // shares are shares of what's left after it. Its cells print empty,
        // exactly as the paper form does.
        numbered: true,
        numberHeader: "No.",
        headerFontPt: 10,
        // Widths are the PDF's, with 0.9% moved from the two roomy text
        // columns to Frequency and Ongoing?. The PDF sets its headers in 7pt;
        // at the 10pt these print at, those two labels no longer fit on one
        // line, and a wrapped header costs the whole table a second header
        // line. 0.4% off a 25% column is invisible; a three-line header band
        // is not. See "keeps every Con Med header label on a single line".
        columns: [
          { label: "Medication / Product Name", type: "Text", width: 24.9 },
          { label: "Indication", sublabel: "(Reason for use)", type: "Text", width: 24.8 },
          { label: "Dose", type: "Text", width: 8.4 },
          { label: "Unit", type: "Text", width: 4.5 },
          { label: "Route", type: "Text", width: 4.9 },
          { label: "Frequency", type: "Text", width: 7.3 },
          { label: "Start Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 9.3 },
          // Text, not a Y/N choice: the PDF prints this cell BLANK and says
          // "Y / N" in the header — the coordinator writes the letter. Boxes
          // here would be a different form from the one being replaced.
          { label: "Ongoing?", sublabel: "Y / N", type: "Text", width: 6.6 },
          { label: "Stop Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 9.3 },
        ],
      },
    ],
  },
  {
    id: "medical-surgical-history-log",
    label: "Medical/Surgical History Log",
    orientation: "landscape",
    questions: [
      {
        // The wording from the paper form, verbatim, above the grid. A Select
        // (pick one), so it prints as a single pair of marks — and `markStyle`
        // makes those marks SQUARE, because that is what the form draws. See
        // markStyleFor: the convention is round for pick-one, and this is the
        // documented opt-out for a form that says otherwise.
        type: "Select",
        markStyle: "box",
        question:
          "Are there any relevant medical conditions, surgeries, or procedures that are not documented on the EMR medical/surgical history printout?",
        options: YES_NO,
      },
      {
        type: "Table",
        question: "Medical/Surgical History Log",
        minRows: 9,
        rowHeightPt: 34,
        // The headers ARE the form here — they carry the date formats and the
        // initials prompt — so they print at 11pt rather than the 9pt a
        // visit's inline grid uses.
        headerFontPt: 11,
        columns: [
          { label: "Medical History/Surgical Event", type: "Text", width: 25.6 },
          { label: "Onset Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 11.4 },
          {
            // Prints "❑ Ongoing ❑ Resolved" into EVERY row of the blank form
            // with no prefill needed — a short choice column draws its options
            // as boxes in each cell (columnPrintsBoxes). This IS what the PDF
            // does, unlike the Con Med "Ongoing?" column above, whose cells it
            // leaves blank. MultiSelect rather than Select because the form
            // draws squares, and choiceMarkFor gives Select round pick-one
            // marks. `stack` puts Ongoing on one line and Resolved on the
            // next, as the form does.
            label: "Ongoing/ Resolved",
            type: "MultiSelect",
            options: ["Ongoing", "Resolved"],
            stack: true,
            width: 12.6,
          },
          { label: "End Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 10.3 },
          { label: "Logged by", sublabel: "“Initials”", type: "Text", width: 11.2 },
          { label: "PI/Sub-I Initial and Date", type: "Text", width: 28.9 },
        ],
      },
    ],
  },
  {
    id: "ae-log",
    unavailable: "Coming soon — this log is still being written.",
    label: "AE Log",
    orientation: "landscape",
    questions: [],
  },
  {
    id: "exacerbation-log",
    // Withdrawn, not deleted — the content below is finished and ready, and
    // rebuilding it to bring it back would be the whole job again. Re-enabling
    // is deleting this one line. Same convention as sourceQuestionTemplates.
    unavailable: "Not in use for now.",
    label: "Exacerbation Log",
    orientation: "landscape",
    questions: [
      {
        type: "Table",
        question: "Exacerbation Log",
        minRows: 12,
        // 26pt, not the Con Med 25: this table's header is TWO rows deep (the
        // Corticoids group spans a row of its own), so the budget below has
        // less to give the body — 12 x 26pt still lands well inside one page.
        rowHeightPt: 26,
        headerFontPt: 10,
        // A leading "#" column, cells blank like the Con Med "No." column —
        // the coordinator numbers their own entries.
        numbered: true,
        numberHeader: "#",
        columns: [
          // A single tick box, not a Yes/No pair: an exacerbation either was
          // serious or the box is left empty.
          { label: "Serious", type: "Checkbox", width: 7 },
          // The printed form spans these two under one "Corticoids" header.
          // `group` is what draws that spanning cell, with these two names on
          // a second header line beneath it — see columnHeaderGroups.
          {
            label: "# of injections",
            type: "Number",
            group: "Corticoids",
            width: 11,
          },
          {
            label: "# of days of oral corticosteroids",
            type: "Number",
            group: "Corticoids",
            width: 15,
          },
          // One column, two lines: the question and the follow-up that only
          // matters if the answer is yes. They were two columns, which asked
          // the coordinator to look in two places for one fact.
          {
            label: "Antibiotics (Y/N)",
            sublabel: "If yes, list name",
            type: "Text",
            width: 17,
          },
          // Text, not a Y/N choice — nothing in this table is pre-filled with
          // options; the coordinator writes the answer.
          { label: "Urgent Health Care Provider Visit?", type: "Text", width: 15 },
          { label: "ED visit?", type: "Text", width: 10 },
          { label: "Start Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 12.5 },
          { label: "End Date", sublabel: "(DD-MMM-YYYY)", type: "Date", width: 12.5 },
        ],
      },
    ],
  },
];

/**
 * The same logs, ordered for the "Add Log" MENU: everything available first,
 * everything withdrawn after it.
 *
 * Exactly the rule SOURCE_QUESTION_TEMPLATES_MENU applies to "Use from
 * template", and for the same reason: a withdrawn log can't be picked, so
 * sitting mid-list it is a dead row between two live ones — read, reached for,
 * and only then found to be greyed out. At the bottom it is still visible,
 * which is the whole point of listing it, without interrupting the list of
 * things that work.
 *
 * A separate export rather than a re-ordering of the array above, because that
 * order is the DATA's own (the two transcribed-from-PDF logs together) and
 * lookups by id read from it. Both filters are stable, so logs keep their
 * relative order within each half.
 */
export const SOURCE_LOG_TEMPLATES_MENU = [
  ...SOURCE_LOG_TEMPLATES.filter((t) => !t.unavailable),
  ...SOURCE_LOG_TEMPLATES.filter((t) => t.unavailable),
];

/** One log by id, or undefined. */
export const findLogTemplate = (id) =>
  SOURCE_LOG_TEMPLATES.find((t) => t.id === id);

/**
 * The section + event a log becomes when it is added to a template.
 *
 * Framework-free and Firebase-free so it can be tested on its own, and so the
 * shape a log takes is stated in ONE place rather than being assembled inline
 * by whichever page happens to add one.
 *
 * Returns null for an unavailable log — the single gate every insertion passes
 * through, so a future call site can't get one in behind the disabled menu
 * item (the same guard insertTemplateAt has for a withdrawn question block).
 *
 * @param {string} id
 * @param {{makeSection: Function}} factories  passed in rather than imported so
 *   this module stays a leaf of the schema, matching sourceQuestionTemplates.
 * @returns {object|null} a makeSection-shaped section
 */
export const buildLogSection = (id, { makeSection }) => {
  const log = findLogTemplate(id);
  if (!log || log.unavailable) return null;
  return makeSection({
    name: log.label,
    kind: "log",
    events: [
      {
        eventName: log.label,
        kind: "log",
        ...(log.orientation ? { orientation: log.orientation } : {}),
        questions: (log.questions || []).map((q) => ({
          ...q,
          type: normFieldType(q.type),
        })),
      },
    ],
  });
};
